import { Component, HostListener, Inject, OnInit, PLATFORM_ID, TransferState, afterNextRender, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NotificationsComponent } from './shared/notification/notification.component';
import { HeaderComponent } from './shared/header/header.component';
import { CommonModule, PlatformLocation, isPlatformBrowser } from '@angular/common';
import { MobileFooterMenuComponent } from './shared/mobile-footer-menu/mobile-footer-menu.component';
import { SignalsStoreService } from './shared/signals-store.service';
import { AppService } from './app.service';
import { ResolutionService } from './shared/resolution.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { LocalStorageService } from './shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from './shared/constants/databases';
import { OrderService } from './shared/order.service';
import { KlaviyoService } from './shared/klaviyo/klaviyo.service';
import { ScrollToTopDirective } from './shared/directives/scroll-to-top.directive';
import { ShopSliderBannerComponent } from './shared/shop-slider-banner/shop-slider-banner.component';
import { ShopHeaderComponent } from './shared/shop-header/shop-header.component';
import { ShopBundleListComponent } from './shared/shop-bundle-list/shop-bundle-list.component';
import { GlobalMessageComponent } from './shared/global-message/global-message.component';
import { LoadingComponent } from './shared/loading/loading.component';
import { LoadingService } from './shared/loading.service';
import { PromotionsService } from './shared/promotions.service';
import { MarketingReferral } from './shared/types/common.types';
import { SideCheckoutComponent } from "./side-checkout/side-checkout.component";
import { checkSignupCouponCodes } from './shared/common/singup.utils';
import { SHOP_DATA_KEY } from './resolvers/shop.resolver';
import { CanonicalService } from './shared/canonical.service';
import { AttributionService } from './shared/attribution/attribution.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatProgressBarModule,
    NgbModule,
    NotificationsComponent,
    HeaderComponent,
    CommonModule,
    MobileFooterMenuComponent,
    ScrollToTopDirective,
    ShopSliderBannerComponent,
    ShopHeaderComponent,
    ShopBundleListComponent,
    GlobalMessageComponent,
    LoadingComponent,
    SideCheckoutComponent,
  ],
  providers: [AppService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'ecommerce';

  private signalStoreService = inject(SignalsStoreService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private platformLocation = inject(PlatformLocation);
  private transferState = inject(TransferState);
  private appService = inject(AppService);
  private resolutionService = inject(ResolutionService);
  private localStorageService = inject(LocalStorageService);
  private orderService = inject(OrderService);
  private klaviyoService = inject(KlaviyoService);
  loadingService = inject(LoadingService);

  #promotionsService = inject(PromotionsService);
  #canonicalService = inject(CanonicalService);
  #attributionService = inject(AttributionService);

  private containerClassesToApply = {
    layoutSignup: 'layout-signup',
    layoutStatic: 'layout-static',
    layoutShop: 'layout-shop',
    layoutSettings: 'layout-settings',
    layoutLogin: 'layout-login',
  };

  private containerClassesXRout: any = {
    '/': this.containerClassesToApply.layoutStatic,
    '/pages': this.containerClassesToApply.layoutStatic,
    '/login': this.containerClassesToApply.layoutLogin,
    '/account-recovery': this.containerClassesToApply.layoutLogin,
    '/terms-and-conditions': this.containerClassesToApply.layoutLogin,
    '/settings': this.containerClassesToApply.layoutSettings,
    '/signup': this.containerClassesToApply.layoutSignup,
    '/shop': this.containerClassesToApply.layoutShop,
    '/favorites': this.containerClassesToApply.layoutShop,
    '/buy-again': this.containerClassesToApply.layoutShop,
    '/order': this.containerClassesToApply.layoutShop,
  }

  showAppHeader = signal(false);
  containerClassToAplly = signal('');

  hasSession = this.signalStoreService.hasSession;
  #hasSession$ = toObservable(this.hasSession);
  isSidebarOpen = this.signalStoreService.isSidebarOpen;
  hasGlobalMessages = computed(() => !!this.signalStoreService.globalMessages().length);
  showCartPreview = this.signalStoreService.showCartPreview;

  showAppCartPreview = computed(() => this.setUpCartPreviewDisplay());
  showJustAddedProduct = computed(() => this.setUpJustAddedProductDisplay());

  shouldShowCategoriesBar = computed(() => this.signalStoreService.shouldShowCategoriesBar());

  isMobile = computed(() => this.resolutionService.isMobile());
  routeSignal = signal(this.router.url);
  permissionsSignal = computed(() => this.signalStoreService.permissions());
  isInAuth = computed(() => this.signalStoreService.isInAuth());

  showMobileFooter = this.signalStoreService.isShowingMobileFooter;

  private isMobiles$ = toObservable(this.isMobile);
  private routeSignal$ = toObservable(this.routeSignal);
  private permissionsSignal$ = toObservable(this.permissionsSignal);

  isShopComponent = computed(() => this.signalStoreService.getIsInShopComponent());

  isShopBannerHidden = computed(() => this.signalStoreService.isShopBannerHidden());
  isShopBannerHidden$ = toObservable(this.isShopBannerHidden);
  shouldHideShopBanner = signal(false);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    // Earliest browser-side hook we have: the first navigation has not resolved
    // yet, so window.location still carries the campaign params of the landing
    // URL. The service no-ops on the server and after the first capture.
    this.#attributionService.capture();

    // Hydrate route-driven UI state synchronously so the first paint (SSR + CSR)
    // already includes the header, shop banner and layout class. Without this,
    // those elements are injected after the first navigation event and push
    // .main-container down → large CLS culprit on /shop.
    this.#hydrateRouteDrivenState();

    this.#canonicalService.init();
    this.#identifyKlaviyoUser();
    afterNextRender(() => {
      this.appService.getMaintenanceState();
    })
  }

  #hydrateRouteDrivenState() {
    // With `enabledNonBlocking` initial navigation (Angular 19 default),
    // `router.url` is "/" during AppComponent construction — initial nav
    // hasn't resolved yet. Read the real URL from PlatformLocation, which is
    // request-derived in SSR and `location.pathname` in CSR. Seeding
    // `routeSignal` here also makes the validate* helpers (which read it) work.
    const url = (this.platformLocation.pathname || '/').split('?')[0];
    this.routeSignal.set(url);

    this.showAppHeader.set(this.validateRoutesForHeader());
    this.containerClassToAplly.set(this.setUpContainerClass());
    this.signalStoreService.isInOrderComponent.set(this.validateIsInRouteComponent('/order'));
    this.signalStoreService.isInAuth.set(this.validateRoutesForAuth());

    // isInShopComponent is owned by ShopComponent's lifecycle, but that runs
    // after the router-outlet renders — too late for the initial paint. Set it
    // eagerly here when the URL is a shop *list* so the banner reserves its
    // space in the very first render.
    if (this.#isShopListRoute(url)) this.signalStoreService.setIsInShopComponent(true);
  }

  // Distinguishes a shop *list* route (which shows the banner) from a product
  // detail route. Both live under `/shop/...`, so a naive `url.includes('/shop')`
  // also matched product pages → the banner was reserved on reload/direct-URL
  // entry and then removed once ShopOrProductComponent resolved the page to a
  // product, producing a visible banner flash.
  #isShopListRoute(url: string): boolean {
    if (url.includes('/favorites') || url.includes('/buy-again')) return true;

    const segments = url.split('/').filter(Boolean);
    if (segments[0] !== 'shop') return false;

    // /shop, /shop/:category, /shop/:category/:subcategory are always lists.
    if (segments.length <= 3) return true;

    // /shop/:category/:subcategory/:slug is ambiguous: product detail or a deep
    // subcategory list. The resolver ships its productUrlIndex via TransferState,
    // which the browser has synchronously (inlined by SSR) before this runs — use
    // it to keep reserving banner space for deep lists while skipping products.
    const productUrlIndex: string[] | undefined =
      this.transferState.get<any>(SHOP_DATA_KEY, null)?.data?.productUrlIndex;
    if (!productUrlIndex) return true; // No index to decide → preserve prior behavior.

    const slug = segments.slice(1).join('/'); // category/subcategory/slug
    return !productUrlIndex.includes(slug);
  }

  @HostListener('document:click', ['$event'])
  handleClick(event: any) {
    if (this.showJustAddedProduct()) {
      if (event.target.className.indexOf('just-added-product-notification') > -1) {
        this.signalStoreService.justAddedProduct.set(null)
      }
    }
  }

  ngOnInit(): void {
    // this.appService.getMaintenanceState();
    this.setUpRouteSubscription();

    if (!isPlatformBrowser(this.platformId)) return;
    this.activatedRoute.queryParams.subscribe(params => this.checkMarketingReferral(params));
    this.appService.addClassToBody(environment.config.bodyClass);

    combineLatest([this.isMobiles$, this.routeSignal$, this.permissionsSignal$]).pipe(
      tap(() => this.validateCategoriesBarDisplay())
    ).subscribe()

    this.isShopBannerHidden$.pipe(
      tap((value: boolean) => {
        setTimeout(() => {
          this.shouldHideShopBanner.set(value === true);
        }, 500);
      })
    ).subscribe()
  }

  private setUpRouteSubscription() {
    this.router.events.subscribe(() => {
      // Refresh routeSignal first — the validate*/setUpContainerClass helpers
      // now read it (instead of router.url) so they work during the initial
      // SSR/CSR hydration before navigation completes.
      this.routeSignal.set(this.router.url);
      this.signalStoreService.isInAuth.set(this.validateRoutesForAuth());
      this.showAppHeader.set(this.validateRoutesForHeader());
      // this.showCartPreview.set(this.valiteRoutesForCartPreview());
      this.containerClassToAplly.set(this.setUpContainerClass());
      this.signalStoreService.isInOrderComponent.set(this.validateIsInRouteComponent('/order'));
    });
  }

  private setUpContainerClass(): string {
    // Use routeSignal (seeded from PlatformLocation in #hydrateRouteDrivenState
    // and refreshed by router events) instead of router.url, which is "/" during
    // AppComponent construction with enabledNonBlocking initial navigation.
    const currentUrl = this.routeSignal().split('?')[0];
    for (let key in this.containerClassesXRout) {
      if (key === '/' ? currentUrl === key : currentUrl.includes(key)) {
        return this.containerClassesXRout[key];
      }
    }

    return this.containerClassesToApply.layoutShop; // This class by default;
  }

  validateRoutesForAuth(): boolean {
    const allowedRoutes = [
      '/login',
      '/signup',
      'account-recovery/check-email',
    ];
    return this.validateRoutesToShowComponent(allowedRoutes);
  }

  validateRoutesForHeader(): boolean {
    const allowedRoutes = [
      '/',
      '/order',
      '/pages',
      '/product',
      '/settings',
      '/shop',
      '/favorites',
      '/buy-again',
      '/cart',
      '/welcome'
    ];
    return this.validateRoutesToShowComponent(allowedRoutes);
  }

  // valiteRoutesForCartPreview(): boolean {
  //   const allowedRoutes = [
  //     '/product',
  //     '/shop',
  //     '/favorites',
  //     '/buy-again'
  //   ];
  //   return this.validateRoutesToShowComponent(allowedRoutes, 'custom-box');
  // }

  validateRoutesToShowComponent(allowedRoutes: string[], notAllowedRoute: string = 'null'): boolean {
    const currentUrl = this.routeSignal();
    const cleanedRoute = currentUrl.split('?')[0];
    return allowedRoutes.some(route => (route === '/' ? cleanedRoute === route : cleanedRoute.includes(route)) && !cleanedRoute.includes(notAllowedRoute));
  }

  validateIsInRouteComponent(route: string) {
    const currentUrl = this.routeSignal();
    const cleanedRoute = currentUrl.split('?')[0];
    return cleanedRoute === route;
  }

  private setUpCartPreviewDisplay() {
    const hasSession = this.signalStoreService.hasSession();
    // const order = this.signalStoreService.firebaseOrder();
    // const odooOrder = this.orderService.odooOrder();
    // const hasProducts = order?.products?.common?.length || order?.products?.subscription?.length;
    // const hasOdooProducts = odooOrder?.products?.common?.length || odooOrder?.products?.subscription?.length;
    const shouldShowCartPreview = this.showCartPreview();
    return hasSession && shouldShowCartPreview && !this.isMobile();
  }

  private setUpJustAddedProductDisplay() {
    return this.signalStoreService.hasSession() &&
      this.signalStoreService.justAddedProduct() &&
      this.isMobile();
  }

  private validateCategoriesBarDisplay() {

    const isMobile = this.isMobile();

    const shouldHideHeaderCategoriesNav = !!!(this.signalStoreService.permissions()?.shop.allowed ?? true);

    if (shouldHideHeaderCategoriesNav) {
      this.signalStoreService.shouldShowCategoriesBar.set(false);
    } else {
      const shouldShowCategoriesBar = (!isMobile || this.validateRoutesToShowComponent(['/shop', '/favorites', '/buy-again', '/', '/pages']));
      this.signalStoreService.shouldShowCategoriesBar.set(shouldShowCategoriesBar);
    }

    const shouldShowMobileFooter = isMobile && !shouldHideHeaderCategoriesNav && this.validateRoutesToShowComponent(['/shop', '/favorites', '/buy-again', '/settings', '/order', '/cart']);
    this.showMobileFooter.set(shouldShowMobileFooter);
  }

  #identifyKlaviyoUser() {
    this.#hasSession$.pipe(
      tap((hasSession: boolean) => {
        if (hasSession)
          this.klaviyoService.identiyUser();
      })
    ).subscribe();
  }

  checkMarketingReferral(params: any) {

    let marketingReferral: MarketingReferral;

    const currentMarketingInfo: MarketingReferral | null = this.localStorageService.get(LOCALSTORAGE_KEYS.MARKETING_REFERRAL);

    if (params['utm_source'] && params['utm_medium'] && params['utm_campaign']) {
      marketingReferral = {
        utmCampaign: params['utm_campaign'],
        utmMedium: params['utm_medium'],
        utmSource: params['utm_source'],
      }
    } else if (currentMarketingInfo?.utmCampaign)
      marketingReferral = { ...currentMarketingInfo };
    else return this.signalStoreService.setIsLoadedMarketingValidation(true);

    checkSignupCouponCodes({
      marketingReferral,
      signalsStoreService: this.signalStoreService,
      localStorageService: this.localStorageService,
      promotionsService: this.#promotionsService,
      showDefaultMessage: false
    }).subscribe();

  }
}
